import { PRODUCTS, productByStoreId, type ProductDef, type ProductId } from './products';
import { REVENUECAT_KEY, purchasesAreReal } from './config';

export interface StoreProduct {
  id: ProductId;
  /** localised price exactly as the store formats it – never build it ourselves (§10) */
  priceString: string;
  price: number;
}

export type PurchaseResult =
  | { ok: true; transactionId: string }
  | { ok: false; cancelled: boolean; message?: string };

export interface PurchaseProvider {
  readonly mode: 'test' | 'store';
  init(): Promise<void>;
  products(): Promise<StoreProduct[]>;
  purchase(id: ProductId): Promise<PurchaseResult>;
  /** one-time products the player owns (for "Restore purchases") */
  restore(): Promise<ProductId[]>;
}

/**
 * TEST MODE (web preview, or app without RevenueCat key): a clearly marked
 * test sheet asks "Buy (test)?" – no money is involved. Owned one-time
 * products are remembered in this browser so "Restore" can be tried out.
 */
export class TestPurchaseProvider implements PurchaseProvider {
  readonly mode = 'test' as const;
  /** set by the UI: shows the fake purchase sheet, resolves true on "Buy" */
  confirm: (p: ProductDef, priceString: string) => Promise<boolean> = async () => true;
  private owned = new Set<ProductId>();

  async init(): Promise<void> {
    try {
      for (const id of JSON.parse(localStorage.getItem('cafe-paws.testPurchases') ?? '[]') as ProductId[]) this.owned.add(id);
    } catch {
      /* nothing owned */
    }
  }

  async products(): Promise<StoreProduct[]> {
    return PRODUCTS.map((p) => ({ id: p.id, price: p.fallbackPrice, priceString: `$${p.fallbackPrice.toFixed(2)}` }));
  }

  async purchase(id: ProductId): Promise<PurchaseResult> {
    const p = PRODUCTS.find((x) => x.id === id)!;
    const ok = await this.confirm(p, `$${p.fallbackPrice.toFixed(2)}`);
    if (!ok) return { ok: false, cancelled: true };
    await new Promise((r) => setTimeout(r, 600));
    if (p.oneTime) {
      this.owned.add(id);
      try {
        localStorage.setItem('cafe-paws.testPurchases', JSON.stringify([...this.owned]));
      } catch {
        /* not persisted */
      }
    }
    return { ok: true, transactionId: `test-${id}-${Date.now()}-${Math.floor(Math.random() * 1e6)}` };
  }

  async restore(): Promise<ProductId[]> {
    return [...this.owned];
  }
}

/**
 * Real purchases through RevenueCat (App Store + Google Play). RevenueCat
 * validates every receipt on its servers before reporting a purchase, so the
 * game only grants what the store really charged. Loaded lazily: the web
 * build never touches the native plugin.
 */
export class RevenueCatProvider implements PurchaseProvider {
  readonly mode = 'store' as const;
  private rc?: typeof import('@revenuecat/purchases-capacitor').Purchases;
  private cache = new Map<ProductId, import('@revenuecat/purchases-capacitor').PurchasesStoreProduct>();

  async init(): Promise<void> {
    const mod = await import('@revenuecat/purchases-capacitor');
    this.rc = mod.Purchases;
    await this.rc.configure({ apiKey: REVENUECAT_KEY! });
  }

  async products(): Promise<StoreProduct[]> {
    const { PRODUCT_CATEGORY } = await import('@revenuecat/purchases-capacitor');
    const { products } = await this.rc!.getProducts({
      productIdentifiers: PRODUCTS.map((p) => p.storeId),
      type: PRODUCT_CATEGORY.NON_SUBSCRIPTION,
    });
    const out: StoreProduct[] = [];
    for (const sp of products) {
      const def = productByStoreId(sp.identifier);
      if (!def) continue;
      this.cache.set(def.id, sp);
      out.push({ id: def.id, price: sp.price, priceString: sp.priceString });
    }
    return out;
  }

  async purchase(id: ProductId): Promise<PurchaseResult> {
    const sp = this.cache.get(id);
    if (!sp) return { ok: false, cancelled: false, message: 'This item is not available right now.' };
    try {
      const r = await this.rc!.purchaseStoreProduct({ product: sp });
      return { ok: true, transactionId: r.transaction.transactionIdentifier };
    } catch (e) {
      const err = e as { userCancelled?: boolean | null; message?: string };
      return { ok: false, cancelled: !!err.userCancelled, message: err.message };
    }
  }

  async restore(): Promise<ProductId[]> {
    const { customerInfo } = await this.rc!.restorePurchases();
    return customerInfo.allPurchasedProductIdentifiers
      .map((sid) => productByStoreId(sid))
      .filter((p): p is ProductDef => !!p && p.oneTime)
      .map((p) => p.id);
  }
}

export function createPurchaseProvider(): PurchaseProvider {
  return purchasesAreReal ? new RevenueCatProvider() : new TestPurchaseProvider();
}
